import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import {
  emailValidator,
  emailWOValueValidator,
  MobileNoWOValueValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import { TfiArrowCircleDown } from "react-icons/tfi";
import IconComponent from "../../components/common/IconComponent";
import {
  getAdminSettings,
  updateAdminSettings,
} from "./services/adminsetting.services";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormPasswordField from "../../components/formFields/FormPasswordField";

const AdminSetting: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const [emailOpen, setEmailOpen] = useState(true);
  const [handlingFeeOpen, setHandlingFeeOpen] = useState(true);
  const [TNCOpen, setTNCOpen] = useState(true);

  const [formKey, setFormKey] = React.useState(1);

  const loading = useAppSelector((state) => state.adminsetting.loading);
  const AdminSettingDetails = useAppSelector(
    (state) => state.adminsetting.AdminSettingDetails,
  );

  useEffect(() => {
    dispatch(getAdminSettings());
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [AdminSettingDetails]);

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        TCSUnderPer: values.TCSUnderPer,
        TCSLimit: values.TCSLimit,
        TCSOverPer: values.TCSOverPer,
        FollowupRemienderHrs: values.FollowupRemienderHrs,
        GST: values.GST,
        Dom_Quot_TermsNCondition: values?.Dom_Quot_TermsNCondition,
        AdminEmailID: values?.AdminEmailID,
        AdminMobileNo: values?.AdminMobileNo,
        Int_Quot_TermsNCondition: values?.Int_Quot_TermsNCondition,
        Dom_FIT_TermsNCondition: values?.Dom_FIT_TermsNCondition,
        Int_FIT_TermsNCondition: values?.Int_FIT_TermsNCondition,
        FlightTermsAndCondition: values?.FlightTermsAndCondition,
        HotelTermsAndCondition: values?.HotelTermsAndCondition,
        VisaTermsAndCondition: values?.VisaTermsAndCondition,
        MarkupType: values?.MarkupType || "",
        MarkupValue: values?.MarkupValue,
        DisplayName: values?.DisplayName || "",
        EmailID: values?.EmailID || "",
        Password: values?.Password || "",
      };
      const response = await dispatch(updateAdminSettings(payload));
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getAdminSettings());
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={AdminSettingDetails}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <Typography.h4>{"Admin Setting"}</Typography.h4>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h5
                    style={{
                      marginBottom: "6px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                    onClick={() => setEmailOpen(!emailOpen)}
                  >
                    {"Email Configuration"}
                    <IconComponent
                      icon={TfiArrowCircleDown}
                      style={{
                        transform: emailOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                      className="me-2 chevron-icon"
                    />
                  </Typography.h5>
                </GridLayoutItem>
                {emailOpen && (
                  <>
                    <GridLayoutItem>
                      <Field
                        name="DisplayName"
                        maxLength="100"
                        label="Display Name"
                        placeholder="i.e. John Doe"
                        component={FormTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="EmailID"
                        type="text"
                        label="Email"
                        placeholder="i.e. example@gmail.com"
                        component={FormTextField}
                        validator={emailValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="Password"
                        type="Password"
                        label="Password"
                        placeholder="Password"
                        component={FormPasswordField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="AdminEmailID"
                        type="text"
                        label="Admin Email"
                        placeholder="i.e. example@gmail.com"
                        component={FormTextField}
                        validator={emailWOValueValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="AdminMobileNo"
                        type="number"
                        label="Admin Mobile No."
                        placeholder="i.e. 9999999999"
                        component={FormTextField}
                        validator={MobileNoWOValueValidator}
                      />
                    </GridLayoutItem>
                    {!handlingFeeOpen && emailOpen && (
                      <GridLayoutItem
                        colSpan={3}
                        style={{
                          textAlign: "end",
                          marginTop: 20,
                          width: "100%",
                        }}
                      >
                        <Button
                          type="button"
                          fillMode={"outline"}
                          themeColor={"primary"}
                          style={{ marginRight: 10 }}
                          onClick={() => setEmailOpen(false)}
                        >
                          Cancel
                        </Button>
                        <ButtonWithLoading
                          label={"Save"}
                          type="submit"
                          disabled={!formRenderProps.allowSubmit || loading}
                          loading={loading}
                        />
                      </GridLayoutItem>
                    )}
                  </>
                )}
              </GridLayout>
            </ShadowCard>
            <ShadowCard style={{ padding: 12, margin: "20px 0" }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h5
                    style={{
                      marginBottom: "6px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                    onClick={() => setHandlingFeeOpen(!handlingFeeOpen)}
                  >
                    {"Taxes"}
                    <IconComponent
                      icon={TfiArrowCircleDown}
                      style={{
                        transform: handlingFeeOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                      className="me-2 chevron-icon"
                    />
                  </Typography.h5>
                </GridLayoutItem>
                {handlingFeeOpen && (
                  <>
                    <GridLayoutItem>
                      <Field
                        name="GST"
                        maxLength="100"
                        label="GST Percentage"
                        placeholder="i.e 18"
                        component={FormTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="TCSLimit"
                        maxLength="100"
                        label="TCS Amount Limit"
                        placeholder="i.e 1000000"
                        component={FormTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem></GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="TCSUnderPer"
                        maxLength="100"
                        label="TCS Under Percentage"
                        placeholder="i.e 5"
                        component={FormTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="TCSOverPer"
                        maxLength="100"
                        label="TCS Over Percentage"
                        placeholder="i.e 200"
                        component={FormTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="FollowupRemienderHrs"
                        label="Follow-up Remiender Hrs"
                        placeholder="i.e 2,4"
                        component={FormTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="MarkupType"
                        placeholder="Markup Type"
                        label="Markup Type"
                        astrike={true}
                        validator={requiredValidator}
                        component={FormSelectionField}
                        options={[
                          { label: "₹", value: "₹" },
                          { label: "%", value: "%" },
                        ]}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="right-aligned-field"
                        name="MarkupValue"
                        label="Markup Amount"
                        placeholder="0"
                        component={FormNumericTextField}
                        max={
                          formRenderProps.valueGetter("MarkupType") === "%"
                            ? 100
                            : "unset"
                        }
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      style={{
                        textAlign: "end",
                        marginTop: 35,
                        width: "100%",
                      }}
                    >
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginRight: 10 }}
                        onClick={() => setHandlingFeeOpen(false)}
                      >
                        Cancel
                      </Button>
                      <ButtonWithLoading
                        label={"Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                    </GridLayoutItem>
                  </>
                )}
              </GridLayout>
            </ShadowCard>
            <ShadowCard style={{ padding: 12, margin: "20px 0" }}>
              <GridLayout
                gap={{ rows: 10, cols: 20 }}
                cols={[{ width: "50%" }, { width: "50%" }]}
                style={{ marginRight: 20 }}
              >
                <GridLayoutItem colSpan={2}>
                  <Typography.h5
                    style={{
                      marginBottom: "0",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                    onClick={() => setTNCOpen(!TNCOpen)}
                  >
                    {"Terms & Conditions"}
                    <IconComponent
                      icon={TfiArrowCircleDown}
                      style={{
                        transform: TNCOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                      className="me-2 chevron-icon"
                    />
                  </Typography.h5>
                </GridLayoutItem>
                {TNCOpen && (
                  <>
                    <GridLayoutItem>
                      <Field
                        name="Dom_Quot_TermsNCondition"
                        label="Domestic Quote Terms & Condition"
                        component={FormRichTextField}
                        contentHeight={250}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="Int_Quot_TermsNCondition"
                        label="International Quote Terms & Condition"
                        component={FormRichTextField}
                        contentHeight={250}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="Dom_FIT_TermsNCondition"
                        label="Domestic FIT Booking Terms & Condition"
                        component={FormRichTextField}
                        contentHeight={250}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="Int_FIT_TermsNCondition"
                        label="International FIT Booking Terms & Condition"
                        component={FormRichTextField}
                        contentHeight={250}
                      />
                    </GridLayoutItem>

                    <GridLayoutItem>
                      <Field
                        name="FlightTermsAndCondition"
                        label="Flight Terms & Condition"
                        component={FormRichTextField}
                        contentHeight={250}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="HotelTermsAndCondition"
                        label="Hotel Terms & Condition"
                        component={FormRichTextField}
                        contentHeight={250}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="VisaTermsAndCondition"
                        label="Visa Terms & Condition"
                        component={FormRichTextField}
                        contentHeight={250}
                      />
                    </GridLayoutItem>

                    <GridLayoutItem
                      colSpan={2}
                      style={{
                        textAlign: "end",
                        marginTop: 20,
                        width: "100%",
                      }}
                    >
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginRight: 10 }}
                        onClick={() => setTNCOpen(false)}
                      >
                        Cancel
                      </Button>
                      <ButtonWithLoading
                        label={"Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                    </GridLayoutItem>
                  </>
                )}
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default AdminSetting;
